import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import {
  Dog,
  Trees,
  X,
  Download,
  AlertTriangle,
  BrainCircuit,
  ArrowLeftRight,
  Leaf,
  Bookmark,
  BookmarkX,
  LandPlot,
  BarChart2,
  LogIn,
  Lock,
} from "lucide-react";
import { generatePDF, AIInsightsData } from "../utils/pdfUtils";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { useUserSubscription } from "@/utils/subscriptionUtils";
import UpgradePrompt from "@/components/UpgradePrompt";
import DeforestationTab from "./panels/DeforestationTab";
import LandLossTab from "./panels/LandLossTab";
import DesertificationTab from "./panels/DesertificationTab";
import SpeciesTab from "./panels/SpeciesTab";
import EnrichedDataTab from "./panels/EnrichedDataTab";
import IcebergPanel, { IcebergLocationData } from "./panels/IcebergPanel";
import { useNavigate } from "react-router-dom";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useAuthModal } from "@/context/SupabaseAuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  fetchRegionById,
  fetchEnvironmentalData,
  Region,
  EnvironmentalData,
  saveRegionForUser,
  unsaveRegionForUser,
  isRegionSavedByUser,
  saveIcebergForUser,
  unsaveIcebergForUser,
  isIcebergSavedByUser,
  canSaveRegion,
} from "../services/environmentalDataService";
import { fetchWeatherData } from "@/services/weatherService";
import { fetchRegionAirQuality } from "@/services/airQualityService";
import { getRegionCarbonData } from "@/services/carbonService";
import { generatePrediction } from "../services/predictionService";
import CreditConfirmationModal, {
  useCreditConfirmation,
} from "@/components/credits/CreditConfirmationModal";
import { consumeCredits } from "@/services/creditService";
import { JobType, getCreditCost, getPredictionJobType } from "@/types/credits";
import { useIsMobile } from "@/hooks/use-mobile";

interface RegionPanelProps {
  regionId: string | null;
  onClose: () => void;
}

const RegionPanel: React.FC<RegionPanelProps> = ({ regionId, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [activeTab, setActiveTab] = useState("deforestation");
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isIcebergLoading, setIsIcebergLoading] = useState(false);
  const [region, setRegion] = useState<Region | null>(null);
  const [data, setData] = useState<EnvironmentalData | null>(null);
  const [iceberg, setIceberg] = useState<IcebergLocationData | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [saveInfo, setSaveInfo] = useState<{
    allowed: boolean;
    remaining: number;
    message: string;
  } | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const { setShowLogin } = useAuthModal();
  const { hasAccess, getRestrictionMessage } = useUserSubscription();
  const { modalState, requestConfirmation, closeModal } =
    useCreditConfirmation();
  const isMobile = useIsMobile();
  const [panelWidth, setPanelWidth] = useState(960);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(960);

  const isIcebergMode = regionId?.startsWith("iceberg:") ?? false;
  const icebergKey = isIcebergMode ? regionId?.replace(/^iceberg:/, "") : null;

  useEffect(() => {
    if (regionId) {
      setIsOpen(true);
      if (isIcebergMode && icebergKey) {
        setRegion(null);
        setData(null);
        setIceberg(null);
        loadIcebergData(icebergKey);
      } else {
        setIceberg(null);
        loadRegionData(regionId);
      }
    } else {
      setIsOpen(false);
      setRegion(null);
      setData(null);
      setIceberg(null);
    }
  }, [regionId, icebergKey, isIcebergMode]);

  useEffect(() => {
    const checkIfSaved = async () => {
      if (user && regionId) {
        const saved = isIcebergMode
          ? await isIcebergSavedByUser(user.id, icebergKey!)
          : await isRegionSavedByUser(user.id, regionId);
        setIsSaved(saved);
      } else {
        setIsSaved(false);
      }
    };

    checkIfSaved();
  }, [user, regionId, isIcebergMode]);

  const loadIcebergData = async (icebergId: string) => {
    setIsIcebergLoading(true);
    try {
      const { data, error } = await supabase
        .from("icebergs")
        .select("*")
        .eq("iceberg_id", icebergId)
        .single();

      if (error || !data) {
        throw error || new Error("Iceberg not found");
      }

      const raw = data as Record<string, unknown>;

      // Position, size and observation date come from the most recent tracking_history entry
      type TrackingEntry = {
        latitude: number;
        longitude: number;
        observation_date: string;
        length_nm?: number | null;
        width_nm?: number | null;
        area_sqkm?: number | null;
        area_sqmi?: number | null;
        area_sqnm?: number | null;
        est_mean_draft?: number | null;
        est_volume?: number | null;
        // Per-observation melt metrics (vary across history entries)
        min_est_annual_mass_loss_Gt_per_yr?: number | null;
        melt_data_source?: string | null;
        tracking_uncertainty_percent?: number | null;
        min_avg_annual_thickness_melt_rate_m_per_yr?: number | null;
        min_avg_annual_lateral_retreat_m_per_yr?: number | null;
      };

      const history: TrackingEntry[] = Array.isArray(raw.tracking_history)
        ? (raw.tracking_history as TrackingEntry[])
        : [];

      const latest =
        history.length > 0
          ? history.reduce((a, b) =>
              a.observation_date > b.observation_date ? a : b,
            )
          : null;

      const normalized: IcebergLocationData = {
        iceberg_id: String(raw.iceberg_id),
        latitude: latest != null ? Number(latest.latitude) : 0,
        longitude: latest != null ? Number(latest.longitude) : 0,
        length_nm: latest?.length_nm != null ? Number(latest.length_nm) : null,
        width_nm: latest?.width_nm != null ? Number(latest.width_nm) : null,
        area_sqmi: latest?.area_sqmi != null ? Number(latest.area_sqmi) : null,
        area_sqnm: latest?.area_sqnm != null ? Number(latest.area_sqnm) : null,
        area_sqkm: latest?.area_sqkm != null ? Number(latest.area_sqkm) : null,
        last_update:
          latest?.observation_date != null
            ? String(latest.observation_date)
            : null,
        est_mean_draft:
          latest?.est_mean_draft != null ? Number(latest.est_mean_draft) : null,
        est_volume:
          latest?.est_volume != null ? Number(latest.est_volume) : null,
        // Top-level melt metrics (use special column names from new schema)
        // Prefer latest tracking_history entry value when available (it varies per observation),
        // fall back to top-level column for backwards compatibility.
        melt_data_source:
          latest?.melt_data_source != null
            ? String(latest.melt_data_source)
            : raw.melt_data_source != null
              ? String(raw.melt_data_source)
              : null,
        primary_tracking_agency:
          raw.primary_tracking_agency != null
            ? String(raw.primary_tracking_agency)
            : null,
        min_avg_annual_thickness_melt_rate_m_per_yr:
          latest?.min_avg_annual_thickness_melt_rate_m_per_yr != null
            ? Number(latest.min_avg_annual_thickness_melt_rate_m_per_yr)
            : raw["min_avg_annual_thickness_melt_rate_m/yr"] != null
              ? Number(raw["min_avg_annual_thickness_melt_rate_m/yr"])
              : null,
        max_avg_annual_thickness_melt_rate_m_per_yr:
          raw["max_avg_annual_thickness_melt_rate_m/yr"] != null
            ? Number(raw["max_avg_annual_thickness_melt_rate_m/yr"])
            : null,
        min_avg_annual_lateral_retreat_m_per_yr:
          latest?.min_avg_annual_lateral_retreat_m_per_yr != null
            ? Number(latest.min_avg_annual_lateral_retreat_m_per_yr)
            : raw["min_avg_annual_lateral_retreat_m/yr"] != null
              ? Number(raw["min_avg_annual_lateral_retreat_m/yr"])
              : null,
        max_avg_annual_lateral_retreat_m_per_yr:
          raw["max_avg_annual_lateral_retreat_m/yr"] != null
            ? Number(raw["max_avg_annual_lateral_retreat_m/yr"])
            : null,
        min_est_annual_mass_loss_Gt_per_yr:
          latest?.min_est_annual_mass_loss_Gt_per_yr != null
            ? Number(latest.min_est_annual_mass_loss_Gt_per_yr)
            : raw["min_est_annual_mass_loss_Gt/yr"] != null
              ? Number(raw["min_est_annual_mass_loss_Gt/yr"])
              : null,
        max_est_annual_mass_loss_Gt_per_yr:
          raw["max_est_annual_mass_loss_Gt/yr"] != null
            ? Number(raw["max_est_annual_mass_loss_Gt/yr"])
            : null,
        tracking_uncertainty_percent:
          latest?.tracking_uncertainty_percent != null
            ? Number(latest.tracking_uncertainty_percent)
            : raw["tracking_uncertainty_%"] != null
              ? Number(raw["tracking_uncertainty_%"])
              : null,
        tracking_history: [...history].sort((a, b) =>
          a.observation_date.localeCompare(b.observation_date),
        ),
      };

      setIceberg(normalized);
    } catch (error) {
      console.error("Error loading iceberg data:", error);
      setIceberg(null);
      toast({
        title: "Error loading iceberg",
        description:
          error instanceof Error
            ? error.message
            : "Failed to load iceberg details.",
        variant: "destructive",
      });
    } finally {
      setIsIcebergLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const loadSaveInfo = async () => {
      if (!user) {
        setSaveInfo(null);
        return;
      }

      try {
        const info = await canSaveRegion(user);
        if (mounted) setSaveInfo(info);
      } catch (err) {
        console.error("Failed to load save info:", err);
        if (mounted) setSaveInfo(null);
      }
    };

    loadSaveInfo();
    return () => {
      mounted = false;
    };
  }, [user]);

  useEffect(() => {
    // Removed the handleClickOutside functionality to prevent the panel from being dismissed when clicking outside.
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = dragStartX.current - e.clientX;
      const newWidth = Math.min(
        Math.max(dragStartWidth.current + delta, 400),
        Math.floor(window.innerWidth * 0.9),
      );
      setPanelWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleDragStart = (e: React.MouseEvent) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  const loadRegionData = async (id: string) => {
    setIsLoading(true);
    try {
      // Fetch region data and environmental data in parallel
      const [regionData, envData] = await Promise.all([
        fetchRegionById(id),
        fetchEnvironmentalData(id),
      ]);

      if (!regionData) {
        throw new Error("Region not found");
      }

      setRegion(regionData);
      setData(envData);
    } catch (error) {
      console.error("Error loading region data:", error);
      toast({
        title: "Error loading data",
        description:
          error instanceof Error
            ? error.message
            : "Failed to load region data. Please try again later.",
        variant: "destructive",
      });
      setRegion(null);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (!regionId) {
    return null;
  }

  const handleAuthRequired = (action: () => void) => {
    if (user) {
      action();
    } else {
      setShowAuthDialog(true);
    }
  };

  const handleGeneratePDF = async () => {
    handleAuthRequired(() => {
      if (!region || !data || !user) return;
      // Show credit confirmation modal before running export
      requestConfirmation(
        "MINI_REPORT",
        async () => {
          setIsGeneratingPDF(true);
          // Generate a jobId for idempotency
          const jobId =
            window.crypto && typeof window.crypto.randomUUID === "function"
              ? window.crypto.randomUUID()
              : `${Date.now()}-${Math.random()}`;
          const creditsRequired = getCreditCost("MINI_REPORT");
          try {
            // Fetch AI Insights data if user has access
            let aiData: AIInsightsData | undefined = undefined;
            if (hasAccess("aiInsights")) {
              try {
                const [weather, airQuality, carbon] = await Promise.all([
                  fetchWeatherData(region.id),
                  fetchRegionAirQuality(region.id),
                  getRegionCarbonData(region.id),
                ]);
                aiData = { weather, airQuality, carbon };
              } catch (aiError) {
                console.error(
                  "Error fetching AI insights for report:",
                  aiError,
                );
                // Continue with regular PDF if AI data fails
              }
            }

            await generatePDF(region, data, aiData);
            // Only after successful PDF generation, consume credits
            const consumeResult = await consumeCredits(
              user.id,
              "MINI_REPORT",
              jobId,
              creditsRequired,
            );
            if (!consumeResult.success) {
              toast({
                title: "PDF generated (billing issue)",
                description:
                  "Report was generated but we could not deduct credits. Please contact support.",
                variant: "destructive",
              });
            } else {
              toast({
                title: "PDF Generated",
                description: `Environmental report for ${region.name} has been downloaded.`,
                duration: 3000,
              });
            }
          } catch (error) {
            toast({
              title: "PDF Generation Failed",
              description:
                "There was an error generating the PDF report. No credits were deducted.",
              variant: "destructive",
              duration: 3000,
            });
            console.error("Error generating PDF:", error);
          } finally {
            setIsGeneratingPDF(false);
          }
        },
        {
          title: "Export Mini Report",
          description: `This will generate a mini PDF report for ${region.name}`,
        },
      );
    });
  };

  const handleCompare = () => {
    handleAuthRequired(() => {
      navigate("/dashboard?tab=comparisons");
    });
  };

  const handleEcoPredict = async () => {
    if (!user) {
      setShowAuthDialog(true);
      return;
    }

    if (!hasAccess("aiInsights")) {
      toast({
        title: "Premium Feature",
        description: getRestrictionMessage("aiInsights", "PAYG"),
        variant: "destructive",
      });
      return;
    }

    if (!region || !data) {
      toast({
        title: "Error",
        description: "Region data is not available.",
        variant: "destructive",
      });
      return;
    }

    // Determine correct job type for prediction timeframe
    const predictionJobType = getPredictionJobType("5y");

    // Request credit confirmation modal and run prediction on confirm
    requestConfirmation(
      predictionJobType,
      async () => {
        try {
          const effectiveTimeframe = hasAccess("aiInsights") ? "5y" : "5y";
          const prediction = await generatePrediction(
            region.id,
            data,
            effectiveTimeframe as any,
            user?.id,
          );

          if (!prediction) {
            toast({
              title: "Error",
              description: "Failed to generate prediction",
              variant: "destructive",
            });
            return;
          }

          // NOTE: The prediction API currently performs credit deduction server-side.
          // Avoid calling `consumeCredits` here to prevent double-deductions.

          // Store and navigate similar to Dashboard flow
          localStorage.setItem("currentPrediction", JSON.stringify(prediction));
          navigate("/dashboard", {
            state: { prediction, region_id: region.id },
          });
          toast({
            title: "Success",
            description: `Prediction generated for ${region.name}`,
          });
        } catch (err) {
          console.error("Prediction failed:", err);
          toast({
            title: "Error",
            description: "Failed to generate prediction. Please try again.",
            variant: "destructive",
          });
        }
      },
      {
        title: "Generate Prediction",
        description: `This will generate an AI prediction for ${region.name}`,
      },
    );
  };

  const handleToggleSave = async () => {
    if (!user) {
      setShowAuthDialog(true);
      return;
    }

    try {
      if (isSaved) {
        if (isIcebergMode) {
          await unsaveIcebergForUser(user.id, icebergKey!);
        } else {
          await unsaveRegionForUser(user.id, regionId!);
        }
        setIsSaved(false);
        toast({
          title: isIcebergMode ? "Iceberg Removed" : "Region Removed",
          description: isIcebergMode
            ? "Iceberg has been removed from your saved list."
            : "Region has been removed from your saved regions.",
        });
      } else {
        if (isIcebergMode) {
          await saveIcebergForUser(user.id, icebergKey!);
        } else {
          await saveRegionForUser(user.id, regionId!);
        }
        setIsSaved(true);
        toast({
          title: isIcebergMode ? "Iceberg Saved" : "Region Saved",
          description: isIcebergMode
            ? "Iceberg has been added to your saved list."
            : "Region has been added to your saved regions.",
        });
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update saved regions.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleIcebergReport = () => {
    handleAuthRequired(() => {
      toast({
        title: "Coming Soon",
        description:
          "PDF report export for icebergs will be available in a future update.",
      });
    });
  };

  const handleIcebergPredict = () => {
    handleAuthRequired(() => {
      if (!hasAccess("aiInsights")) {
        toast({
          title: "Premium Feature",
          description: getRestrictionMessage("aiInsights", "PAYG"),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Coming Soon",
        description:
          "EcoPredict iceberg projections will be available in a future update.",
      });
    });
  };

  const panelClasses = cn(
    "fixed flex flex-col bg-background/95 backdrop-blur-xl border-border/50 shadow-2xl z-[9999] overflow-hidden",
    "animate-in duration-300 ease-out",
    isMobile
      ? cn(
          "inset-x-0 bottom-0 rounded-t-2xl border-t max-h-[88vh]",
          isOpen ? "translate-y-0 slide-in-from-bottom" : "translate-y-full",
        )
      : cn(
          "inset-y-0 right-0 border-l",
          isOpen ? "translate-x-0 slide-in-from-right" : "translate-x-full",
        ),
  );

  if (isIcebergMode) {
    if (isIcebergLoading) {
      return createPortal(
        <div
          className={panelClasses}
          style={isMobile ? undefined : { width: panelWidth }}
        >
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-primary">
                Loading iceberg...
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="hover:bg-primary/10"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex items-center justify-center h-[400px]">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">
                  Loading iceberg details...
                </p>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      );
    }

    if (!iceberg) {
      return createPortal(
        <div
          className={panelClasses}
          style={isMobile ? undefined : { width: panelWidth }}
        >
          <Button
            className="absolute top-4 right-4"
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
          <div className="flex flex-col items-center justify-center p-8 rounded-lg border border-dashed border-border bg-card min-h-[400px] text-center space-y-4 mt-[4rem] ml-4 mr-4">
            <AlertTriangle className="h-16 w-16 text-orange-500" />
            <h2 className="text-[3rem] font-semibold text-center ml-auto mr-auto">
              Iceberg Not Found
            </h2>
            <p className="ml-auto mr-auto">
              The requested iceberg could not be loaded from Supabase.
            </p>
          </div>
        </div>,
        document.body,
      );
    }

    return createPortal(
      <>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] animate-in fade-in duration-200"
              onClick={onClose}
            />
            <div
              className={cn(
                "fixed flex flex-col bg-background/95 backdrop-blur-xl border-border/50 shadow-2xl z-[9999] overflow-hidden animate-in duration-300",
                isMobile
                  ? "inset-x-0 bottom-0 rounded-t-2xl border-t max-h-[88vh] slide-in-from-bottom"
                  : "inset-y-0 right-0 border-l slide-in-from-right",
              )}
              style={isMobile ? undefined : { width: panelWidth }}
            >
              {isMobile && (
                <div className="mx-auto mt-3 mb-1 h-1.5 w-12 rounded-full bg-muted-foreground/30 flex-shrink-0" />
              )}
              {!isMobile && (
                <div
                  className="absolute left-0 inset-y-0 w-2 cursor-ew-resize z-10 flex items-center group"
                  onMouseDown={handleDragStart}
                  title="Drag to resize"
                >
                  <div className="h-12 w-1 mx-auto bg-border/50 group-hover:bg-primary/40 transition-colors rounded-r" />
                </div>
              )}
              <div className="relative bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-border/30">
                <div className="absolute inset-0 bg-grid-white/[0.02] bg-grid-16"></div>
                <div className="relative flex items-center justify-between p-6">
                  <div className="space-y-1 text-left">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                      Iceberg {iceberg.iceberg_id}
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Latitude {iceberg.latitude.toFixed(2)}°</span>
                      <span>•</span>
                      <span>Longitude {iceberg.longitude.toFixed(2)}°</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="h-10 w-10 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              {/* Quick Actions */}
              <div className="bg-muted/30 rounded-xl p-4 space-y-4 mx-6 mt-4 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Quick Actions</h3>
                </div>

                {/* Primary Actions */}
                <div className="flex flex-wrap gap-3">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="default"
                        size="default"
                        className={cn(
                          "shadow-md",
                          hasAccess("aiInsights")
                            ? "bg-primary hover:bg-muted/80 hover:text-black dark:hover:text-white text-primary-foreground font-semibold"
                            : "bg-muted hover:bg-muted/80 text-muted-foreground cursor-not-allowed font-semibold",
                        )}
                        onClick={handleIcebergPredict}
                        disabled={!hasAccess("aiInsights")}
                      >
                        <BrainCircuit className="h-4 w-4 mr-2" />
                        !Hū Projection
                        {!user && (
                          <span className="text-xs opacity-75 ml-1">
                            (Login required)
                          </span>
                        )}
                        {user && !hasAccess("aiInsights") && (
                          <span className="text-xs opacity-75 ml-1">
                            (Premium)
                          </span>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="max-w-xs">
                        <div className="font-medium">
                          This will use 3 credits
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Generates a new EcoPredict output including 5- and
                          10-year projections. Credits are deducted only after
                          successful completion.
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>

                  <Button
                    variant="outline"
                    size="default"
                    className="border-2 hover:bg-accent"
                    onClick={handleToggleSave}
                    disabled={saveInfo ? !saveInfo.allowed : false}
                  >
                    {isSaved ? (
                      <>
                        <BookmarkX className="h-4 w-4 mr-2" />
                        Remove
                      </>
                    ) : (
                      <>
                        <Bookmark className="h-4 w-4 mr-2" />
                        Save Iceberg
                      </>
                    )}
                  </Button>

                  {user ? (
                    saveInfo ? (
                      saveInfo.allowed ? (
                        <span className="text-[.75rem] text-green-600 max-w-xs mt-auto mb-auto">
                          Remaining: {saveInfo.remaining}
                        </span>
                      ) : (
                        <span className="text-[.75rem] text-destructive max-w-xs mt-auto mb-auto">
                          {saveInfo.message}
                        </span>
                      )
                    ) : null
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      Login to save icebergs
                    </span>
                  )}
                </div>

                {/* Secondary Actions */}
                <div className="flex flex-wrap gap-3 pt-2 border-t border-border/50">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleIcebergReport}
                    className="text-sm"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">
                <div className="p-6 space-y-6">
                  <IcebergPanel iceberg={iceberg} />
                </div>
              </div>
            </div>
          </>
        )}
      </>,
      document.body,
    );
  }

  if (isLoading) {
    return createPortal(
      <div
        className={panelClasses}
        style={isMobile ? undefined : { width: panelWidth }}
      >
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-primary">Loading...</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="hover:bg-primary/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex items-center justify-center h-[400px]">
            <Progress value={75} className="h-1 w-[80%]" />
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  if (!region || !data) {
    return createPortal(
      <div
        className={panelClasses}
        style={isMobile ? undefined : { width: panelWidth }}
      >
        <Button
          className="absolute top-4 right-4"
          variant="ghost"
          size="icon"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </Button>
        <div className="flex flex-col items-center justify-center p-8 rounded-lg border border-dashed border-border bg-card min-h-[400px] text-center space-y-4 mt-[4rem] ml-4 mr-4">
          <AlertTriangle className="h-16 w-16 text-orange-500" />
          <h2 className="text-[3rem] font-semibold text-center ml-auto mr-auto">
            No Data Available
          </h2>
          <p className="ml-auto mr-auto">
            The requested region data could not be found.
          </p>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <>
      {isOpen && (
        <>
          {/* Full-screen overlay — dismisses panel on outside click */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] animate-in fade-in duration-200"
            onClick={onClose}
          />
          {/* Panel content */}
          <div
            className={cn(
              "fixed flex flex-col bg-background/95 backdrop-blur-xl border-border/50 shadow-2xl z-[9999] overflow-hidden animate-in duration-300",
              isMobile
                ? "inset-x-0 bottom-0 rounded-t-2xl border-t max-h-[88vh] slide-in-from-bottom"
                : "inset-y-0 right-0 border-l slide-in-from-right",
            )}
            style={isMobile ? undefined : { width: panelWidth }}
          >
            {/* Mobile drag pill */}
            {isMobile && (
              <div className="mx-auto mt-3 mb-1 h-1.5 w-12 rounded-full bg-muted-foreground/30 flex-shrink-0" />
            )}
            {/* Desktop drag-to-resize handle (left edge) */}
            {!isMobile && (
              <div
                className="absolute left-0 inset-y-0 w-2 cursor-ew-resize z-10 flex items-center group"
                onMouseDown={handleDragStart}
                title="Drag to resize"
              >
                <div className="h-12 w-1 mx-auto bg-border/50 group-hover:bg-primary/40 transition-colors rounded-r" />
              </div>
            )}
            {/* Modern Header with Gradient */}
            <div className="relative bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-border/30">
              <div className="absolute inset-0 bg-grid-white/[0.02] bg-grid-16"></div>
              <div className="relative flex items-center justify-between p-6">
                <div className="space-y-1 text-left">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                    {region.name}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{region.country}</span>
                    <span>•</span>
                    <span>{region.continent}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="h-10 w-10 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">
              <div className="p-6 space-y-6">
                {isLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="flex flex-col items-center space-y-4">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                      <p className="text-sm text-muted-foreground">
                        Loading environmental data...
                      </p>
                    </div>
                  </div>
                ) : !region ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center space-y-4">
                    <AlertTriangle className="h-16 w-16 text-muted-foreground/50" />
                    <div>
                      <h3 className="text-lg font-medium mb-2">
                        Region Not Found
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        The selected region could not be found.
                      </p>
                    </div>
                  </div>
                ) : !data ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center space-y-4">
                    <AlertTriangle className="h-16 w-16 text-muted-foreground/50" />
                    <div>
                      <h3 className="text-lg font-medium mb-2">
                        No Data Available
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Environmental data for this region is not available at
                        the moment.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Modern Action Bar */}
                    <div className="bg-muted/30 rounded-xl p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">Quick Actions</h3>
                        {isGeneratingPDF && (
                          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                            <span>Generating report...</span>
                          </div>
                        )}
                      </div>

                      {/* Primary Actions */}
                      <div className="flex flex-wrap gap-3">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="default"
                              size="default"
                              className={cn(
                                "shadow-md",
                                hasAccess("aiInsights")
                                  ? "bg-primary hover:bg-muted/80 hover:text-black dark:hover:text-white text-primary-foreground font-semibold"
                                  : "bg-muted hover:bg-muted/80 text-muted-foreground cursor-not-allowed font-semibold",
                              )}
                              onClick={handleEcoPredict}
                              disabled={!hasAccess("aiInsights")}
                            >
                              <BrainCircuit className="h-4 w-4 mr-2" />
                              !Hū Projection
                              {!user && (
                                <span className="text-xs opacity-75 ml-1">
                                  (Login required)
                                </span>
                              )}
                              {user && !hasAccess("aiInsights") && (
                                <span className="text-xs opacity-75 ml-1">
                                  (Premium)
                                </span>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="max-w-xs">
                              <div className="font-medium">
                                This will use 3 credits
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                Generates a new EcoPredict output including 5-
                                and 10-year projections. Credits are deducted
                                only after successful completion.
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>

                        <Button
                          variant="outline"
                          size="default"
                          className="border-2 hover:bg-accent"
                          onClick={handleToggleSave}
                          disabled={saveInfo ? !saveInfo.allowed : false}
                        >
                          {isSaved ? (
                            <>
                              <BookmarkX className="h-4 w-4 mr-2" />
                              Remove
                            </>
                          ) : (
                            <>
                              <Bookmark className="h-4 w-4 mr-2" />
                              Save Region
                            </>
                          )}
                        </Button>

                        {user ? (
                          saveInfo ? (
                            saveInfo.allowed ? (
                              <span className="text-[.75rem] text-green-600 max-w-xs mt-auto mb-auto">
                                Remaining: {saveInfo.remaining}
                              </span>
                            ) : (
                              <span className="text-[.75rem] text-destructive max-w-xs mt-auto mb-auto">
                                {saveInfo.message}
                              </span>
                            )
                          ) : null
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            Login to save regions
                          </span>
                        )}
                      </div>

                      {/* Secondary Actions */}
                      <div className="flex flex-wrap gap-3 pt-2 border-t border-border/50">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleGeneratePDF}
                          disabled={isGeneratingPDF}
                          className="text-sm"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {isGeneratingPDF ? "Generating..." : "Export PDF"}
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCompare}
                          className="text-sm"
                        >
                          <ArrowLeftRight className="h-4 w-4 mr-2" />
                          Compare Regions
                        </Button>
                      </div>
                    </div>

                    {/* Modern Tab Navigation */}
                    <div className="space-y-4">
                      <Tabs
                        value={activeTab}
                        onValueChange={setActiveTab}
                        className="w-full"
                      >
                        <TabsList className="grid w-full grid-cols-5 bg-muted/50 p-1 h-12 rounded-lg">
                          {/* Regional Insights — plain button when locked to avoid shadcn overrides */}
                          {!user ? (
                            <button
                              type="button"
                              onClick={() => setActiveTab("enriched")}
                              className="w-full inline-flex items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-sm font-medium transition-all bg-amber-100 text-amber-800  hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 hover:bg-gray-100 transition-colors"
                            >
                              <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="hidden sm:inline font-medium truncate">
                                Regional Insights
                              </span>
                            </button>
                          ) : (
                            <TabsTrigger
                              value="enriched"
                              className="relative flex items-center justify-center gap-1.5 transition-all overflow-hidden bg-primary/5 data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-600 data-[state=active]:shadow-md data-[state=active]:border data-[state=active]:border-primary/30 transition-all hover:bg-teal-500/10 dark:hover:bg-muted/80"
                            >
                              <BarChart2 className="h-4 w-4 flex-shrink-0 text-primary" />
                              <span className="hidden sm:inline font-medium truncate">
                                Regional Insights
                              </span>
                            </TabsTrigger>
                          )}
                          <TabsTrigger
                            value="deforestation"
                            className="flex items-center justify-center gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all hover:bg-muted/80 hover:opacity-95 hover:shadow-sm"
                          >
                            <Trees className="h-4 w-4 flex-shrink-0" />
                            <span className="hidden sm:inline font-medium">
                              Forests
                            </span>
                          </TabsTrigger>
                          <TabsTrigger
                            value="landloss"
                            className="flex items-center justify-center gap-2 hover:bg-teal-500/10 data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-600 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all hover:bg-teal-500/10 dark:hover:bg-muted/80"
                          >
                            <LandPlot className="h-4 w-4 flex-shrink-0" />
                            <span className="hidden sm:inline font-medium">
                              Land
                            </span>
                          </TabsTrigger>
                          <TabsTrigger
                            value="desertification"
                            className="flex items-center justify-center gap-2 hover:bg-teal-500/10 data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-600 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all hover:bg-teal-500/10 dark:hover:bg-muted/80"
                          >
                            <Leaf className="h-4 w-4 flex-shrink-0" />
                            <span className="hidden sm:inline font-medium">
                              Desert
                            </span>
                          </TabsTrigger>
                          <TabsTrigger
                            value="species"
                            className="flex items-center justify-center gap-2 hover:bg-teal-500/10 data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-600 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all hover:bg-teal-500/10 dark:hover:bg-muted/80"
                          >
                            <Dog className="h-4 w-4 flex-shrink-0" />
                            <span className="hidden sm:inline font-medium">
                              Species
                            </span>
                          </TabsTrigger>
                        </TabsList>

                        {/* Tab Content with improved spacing */}
                        <div className="mt-6">
                          <TabsContent
                            value="enriched"
                            className="space-y-0 mt-0"
                          >
                            {!user ? (
                              /* ── Unauthenticated gate ── */
                              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                                {/* Icon stack */}
                                <div className="relative mb-6">
                                  <div className="h-24 w-24 rounded-full bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center">
                                    <BarChart2 className="h-11 w-11 text-primary/70" />
                                  </div>
                                  <div className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-primary flex items-center justify-center shadow-md">
                                    <Lock className="h-4 w-4 text-primary-foreground" />
                                  </div>
                                </div>

                                {/* Copy */}
                                <div className="space-y-2 max-w-sm mb-8">
                                  <h3 className="text-xl font-semibold tracking-tight">
                                    Sign in to access Regional Insights
                                  </h3>
                                  <p className="text-sm text-muted-foreground leading-relaxed">
                                    Get live air quality readings, historical
                                    climate trends, carbon emissions analytics,
                                    and extreme weather event summaries — all in
                                    one place.
                                  </p>
                                </div>

                                {/* CTA */}
                                <Button
                                  size="lg"
                                  className="w-full max-w-xs font-semibold shadow-md"
                                  onClick={() => setShowAuthDialog(true)}
                                >
                                  <LogIn className="h-4 w-4 mr-2" />
                                  Login / Sign Up
                                </Button>

                                <p className="mt-4 text-xs text-muted-foreground">
                                  A Standard or Professional subscription is
                                  required after signing in.
                                </p>
                              </div>
                            ) : hasAccess("aiInsights") ? (
                              <EnrichedDataTab regionId={regionId} />
                            ) : (
                              <UpgradePrompt
                                title="Regional Insights Premium Feature"
                                description="Access enriched environmental data for any region, including live air quality, historical climate trends, carbon emissions analytics, and extreme weather events."
                                featureName="Regional Environmental Analytics"
                                className="m-4"
                              />
                            )}
                          </TabsContent>

                          <TabsContent
                            value="deforestation"
                            className="space-y-0 mt-0"
                          >
                            <DeforestationTab data={data} />
                          </TabsContent>

                          <TabsContent
                            value="landloss"
                            className="space-y-0 mt-0"
                          >
                            <LandLossTab data={data} />
                          </TabsContent>

                          <TabsContent
                            value="desertification"
                            className="space-y-0 mt-0"
                          >
                            <DesertificationTab data={data} />
                          </TabsContent>

                          <TabsContent
                            value="species"
                            className="space-y-0 mt-0"
                          >
                            <SpeciesTab data={data} />
                          </TabsContent>
                        </div>
                      </Tabs>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Authentication Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="sm:max-w-[425px] z-[10000]">
          <DialogHeader>
            <DialogTitle>Authentication Required</DialogTitle>
            <DialogDescription>
              You need to create an account or sign in to access this feature.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Creating an account allows you to export PDF reports, save
              regions, and access AI-powered predictions.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                className="w-full"
                onClick={() => {
                  onClose();
                  setShowLogin(true);
                }}
              >
                Login / Sign Up
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Credit confirmation modal for credit-consuming actions (EcoPredict) */}
      <CreditConfirmationModal
        open={modalState.open}
        onOpenChange={(open) => {
          if (!open) closeModal();
        }}
        jobType={modalState.jobType}
        userId={user?.id || ""}
        onConfirm={modalState.onConfirm ?? (async () => {})}
        title={modalState.title}
        description={modalState.description}
      />
    </>,
    document.body,
  );
};

export default RegionPanel;
